import { useEffect, useMemo, useState } from 'react'

import { usePrintScale } from '@editor/hooks/usePrintScale'
import { MeasurementLabelProps } from '@editor/interfaces/canvas/measurementLabel'
import { DrawingParameterMeasurementLabel, DrawingType } from '@editor/interfaces/drawing'
import { CanvasConfigV2 } from '@editor/interfaces/editor'
import { DEFAULT_STYLES, Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import useDrawingStateCheck from '@editor/pages/projects/blueprint-editor/hooks/useDrawingStateCheck'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import { updateDrawingParameters } from '@editor/pages/projects/blueprint-editor/store/drawings'
import { getDrawingsOfType } from '@editor/services/Drawing'
import { convertPrintDimension } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'

import useEventHandlers from './useEventHandlers'

const useMainCanvas = (): CanvasConfigV2 => {
  // Store
  const dispatch = useAppDispatch()
  const hiddenElementIds = useSelector((state: RootState) => state.toolUtil.core.hiddenElementIds)

  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)
  const drawings = useSelector((state: RootState) => state.blueprint.drawings.document.drawings)
  const renderSkipUuids = useSelector((state: RootState) => state.blueprint.drawings.renderSkipUuids)
  const isAllowedToModify = useSelector((state: RootState) => state.blueprint.editor.isAllowedToModify)

  // Hooks
  const { handleMouseUp, handleCancel } = useEventHandlers()
  const { currentPrintScale } = usePrintScale()
  const { editingElementId, workingElementId, isSelectable } = useDrawingStateCheck()
  const { calculateOffsetMatrix } = useProjectionPlane()

  // States
  const [isVisibleOnMode, setIsVisibleOnMode] = useState(false)

  /**
   * The actual visibility can be a bit too quick causing it to render before
   * the camera is switched over. Delay the visibility change to ensure
   * the camera is ready.
   */
  useEffect(() => {
    const check = mode !== Mode.Editor3D

    // The change to hide is immediate, but the change to show is delayed
    if (!check) {
      setIsVisibleOnMode(check)
      return
    }

    setTimeout(() => {
      setIsVisibleOnMode(check)
    }, 50)
  }, [mode])

  return {
    objects: {
      measurementLabels: [
        ...useMemo(
          () =>
            getDrawingsOfType<DrawingParameterMeasurementLabel>(
              drawings,
              DrawingType.MeasurementLabel,
              renderSkipUuids,
            ).map<MeasurementLabelProps>(({ drawing_parameters: { uuid, points, styles } }) => ({
              id: uuid,
              points,
              offsetTransformation: calculateOffsetMatrix(drawings.length + 10).toArray(), // Annotations is always on top
              offset: styles?.textOffset ?? DEFAULT_STYLES.textOffset,
              visible: isVisibleOnMode && !hiddenElementIds.includes(uuid),
              editable: isSelectable(uuid) && isAllowedToModify,
              printScale: currentPrintScale,
              text: {
                color: styles?.textColor ?? DEFAULT_STYLES.textColor,
                size: styles?.textSize ?? DEFAULT_STYLES.textSize,
              },
              line: {
                color: styles?.strokeColor,
                style: styles?.strokeStyle ?? DEFAULT_STYLES.strokeStyle,
                thickness: convertPrintDimension(styles?.strokeWidth || 1, currentPrintScale),
              },
              isEditing: editingElementId === uuid,
              isWorking: workingElementId === uuid,
              onUpdated: ({ id, points: newPoints, offset }) => {
                dispatch(
                  updateDrawingParameters({
                    uuid: id,
                    points: newPoints ?? points,
                    styles: {
                      ...styles,
                      textOffset: offset ?? styles?.textOffset ?? DEFAULT_STYLES.textOffset,
                    },
                  }),
                )
              },
            })),
          [
            drawings,
            renderSkipUuids,
            hiddenElementIds,
            editingElementId,
            workingElementId,
            currentPrintScale,
            isVisibleOnMode,
            isAllowedToModify,
            calculateOffsetMatrix,
            isSelectable,
            dispatch,
          ],
        ),
      ],
    },
    events: {
      onMouseUp: handleMouseUp,
      onCancel: handleCancel,
    },
  }
}

export default useMainCanvas
